import { motion } from "framer-motion";
import { Check, Loader2, RefreshCw, X } from "lucide-react";

import type { PendingMessage } from "@/hooks/use-send-message";
import { cn, relativeTime } from "@/lib/utils";

interface PendingMessageBubbleProps {
  pending: PendingMessage;
  mobileMode?: boolean;
  onRetry: (tempId: string) => void;
  onDismiss: (tempId: string) => void;
}

const SETTLE_SHADOW_KEYFRAMES = [
  "0 0 0 0 rgba(59, 130, 246, 0)",
  "0 0 24px 4px rgba(59, 130, 246, 0.35)",
  "0 0 12px 2px rgba(255, 107, 53, 0.20)",
  "0 0 0 0 rgba(255, 107, 53, 0)",
];

export function PendingMessageBubble({
  pending,
  mobileMode = false,
  onRetry,
  onDismiss,
}: PendingMessageBubbleProps) {
  const isFailed = pending.status === "failed";
  const isSending = pending.status === "sending";
  const isSent = pending.status === "sent";

  return (
    <div className="flex flex-row-reverse gap-3">
      <div className="shrink-0">
        <div className="flex size-8 items-center justify-center rounded-full bg-surface-3 text-[10px] font-semibold text-muted-foreground">
          TÚ
        </div>
      </div>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{
          scale: 1,
          opacity: isSending ? 0.6 : 1,
          boxShadow: isSent
            ? SETTLE_SHADOW_KEYFRAMES
            : "0 0 0 0 rgba(0,0,0,0)",
        }}
        transition={{
          scale: { duration: 0.2 },
          opacity: { duration: 0.2 },
          boxShadow: isSent
            ? { duration: 0.8, times: [0, 0.3, 0.7, 1] }
            : { duration: 0 },
        }}
        className={cn(
          "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
          mobileMode ? "max-w-[85%]" : "max-w-[75%]",
          isFailed
            ? "border border-destructive/40 bg-destructive/[0.06] text-foreground"
            : "bg-surface-3 text-foreground"
        )}
      >
        <p>{pending.content}</p>

        {isFailed && pending.errorMessage && (
          <p className="mt-1 text-xs font-normal opacity-70">
            {pending.errorMessage}
          </p>
        )}

        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground">
          {isSending && <Loader2 className="size-3 animate-spin" />}
          {isSent && <Check className="size-3 text-success" />}
          <time>{relativeTime(pending.createdAt)}</time>
        </div>

        {isFailed && (
          <div className="mt-2 flex gap-1.5">
            {pending.isTransient && (
              <button
                onClick={() => onRetry(pending.tempId)}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-[11px] font-medium text-foreground hover:border-border-strong"
              >
                <RefreshCw className="size-3" />
                Reintentar
              </button>
            )}
            <button
              onClick={() => onDismiss(pending.tempId)}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-2 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
            >
              <X className="size-3" />
              Descartar
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
